import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  getGetClaimReceiptClaimIdGetQueryKey,
  getGetMileageClaimMileageClaimIdGetQueryKey,
  useCancelMileageClaimMileageMileageClaimIdDelete,
  useCancelReceiptReceiptClaimIdDelete,
} from "@/api-v3";
import { useErrorHandler } from "@/hooks/use-error-handler";
import { useToast } from "@/hooks/use-toast";
import { AppRoute } from "@/types/routes";

import type { PaymentKind } from "../types";

type CancelablePaymentKind = Extract<PaymentKind, "mileage" | "receipt">;

interface UseCancelClaimOptions {
  paymentKind: CancelablePaymentKind;
  id: string;
}

export interface CancelClaimHandlers {
  onCancel: () => void;
  isPending: boolean;
}

export const useCancelClaim = ({
  paymentKind,
  id,
}: UseCancelClaimOptions): CancelClaimHandlers => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleMutationError } = useErrorHandler();

  const handleSuccess = () => {
    // removeQueries (not invalidate) so revisiting the page refetches from
    // scratch rather than briefly showing the cached, now-cancelled claim.
    const detailKey =
      paymentKind === "receipt"
        ? getGetClaimReceiptClaimIdGetQueryKey(id)
        : getGetMileageClaimMileageClaimIdGetQueryKey(id);
    queryClient.removeQueries({ queryKey: detailKey });
    queryClient.removeQueries({
      predicate: (query) => {
        const root = query.queryKey[0];
        const url = Array.isArray(root) ? root[0] : root;
        return typeof url === "string" && url.startsWith("/payments");
      },
    });

    toast({ description: t("claimDetail_cancelClaimToast") });
    navigate(AppRoute.PAYMENTS);
  };

  const receiptMutation = useCancelReceiptReceiptClaimIdDelete({
    mutation: { onError: handleMutationError },
  });
  const mileageMutation = useCancelMileageClaimMileageMileageClaimIdDelete({
    mutation: { onError: handleMutationError },
  });

  const isPending = receiptMutation.isPending || mileageMutation.isPending;

  const onCancel = () => {
    if (paymentKind === "receipt") {
      receiptMutation.mutate(
        { receiptClaimId: id },
        { onSuccess: handleSuccess }
      );
      return;
    }

    mileageMutation.mutate(
      { mileageClaimId: id },
      { onSuccess: handleSuccess }
    );
  };

  return { onCancel, isPending };
};
